import { useMemo } from "react";

const defaultState = {
  rows: [],
  timeLabels: [],
  minValue: null,
  maxValue: null,
  avgValue: null,
};

const useHeatmapData = (values, timestamps) => {
  const processed = useMemo(() => {
    if (!values || !timestamps || !values.length || !timestamps.length) {
      return defaultState;
    }

    const weekdayAverages = new Map();
    const WEEKDAYS = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    // Initialize map for all weekdays
    WEEKDAYS.forEach((day) => {
      weekdayAverages.set(day, {
        label: day,
        values: {}, // timeKey -> { sum: 0, count: 0 }
      });
    });

    timestamps.forEach((rawLabel, index) => {
      const parsed = parseTimestampRaw(rawLabel);
      if (!parsed) return;

      const { year, month, day, hours } = parsed;
      const date = new Date(year, parseInt(month, 10) - 1, parseInt(day, 10));
      let dayIdx = date.getDay(); // 0 is Sunday
      if (dayIdx === 0) dayIdx = 7;
      const dayName = WEEKDAYS[dayIdx - 1];

      const timeKey = `${hours}:00`;
      const value = parseNumericValue(values[index]);

      if (value != null) {
        const bucket = weekdayAverages.get(dayName);
        if (!bucket.values[timeKey]) {
          bucket.values[timeKey] = { sum: 0, count: 0 };
        }
        bucket.values[timeKey].sum += value;
        bucket.values[timeKey].count += 1;
      }
    });

    const timeSet = new Set();
    const rows = WEEKDAYS.map((dayName) => {
      const bucket = weekdayAverages.get(dayName);
      // We want all hours 00:00 to 23:00 to be consistent
      const hourLabels = Array.from(
        { length: 24 },
        (_, i) => `${String(i).padStart(2, "0")}:00`,
      );
      hourLabels.forEach((t) => timeSet.add(t));

      return {
        label: dayName,
        values: hourLabels.map((timeKey) => {
          const stats = bucket.values[timeKey];
          return stats && stats.count > 0 ? stats.sum / stats.count : null;
        }),
      };
    });

    const timeLabels = Array.from(timeSet).sort();

    const allValues = rows
      .flatMap((row) => row.values)
      .filter((value) => value != null);
    const minValue = allValues.length ? Math.min(...allValues) : null;
    const maxValue = allValues.length ? Math.max(...allValues) : null;
    const avgValue = allValues.length
      ? allValues.reduce((total, current) => total + current, 0) /
      allValues.length
      : null;

    return {
      rows,
      timeLabels,
      minValue,
      maxValue,
      avgValue,
    };
  }, [values, timestamps]);

  return {
    loading: false,
    error: null,
    hasData: Boolean(processed.rows.length && processed.timeLabels.length),
    ...processed,
  };
};

const parseNumericValue = (value) => {
  if (typeof value === "number") return value;
  if (value == null) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Parse timestamp without timezone conversion - extracts components directly from string
const parseTimestampRaw = (rawLabel) => {
  if (!rawLabel) return null;
  const rawStr = String(rawLabel);
  // Support YYYY-MM-DD HH:mm:ss, YYYY/MM/DD, and T separator
  const match = rawStr.match(
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T\s](\d{1,2}):(\d{1,2})/,
  );
  if (!match) {
    // Try just date if time fails (though less useful for heatmap)
    const dateMatch = rawStr.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return { year, month, day, hours: "00", minutes: "00" };
    }
    return null;
  }
  const [, year, month, day, hours, minutes] = match;
  return {
    year,
    month: month.padStart(2, "0"),
    day: day.padStart(2, "0"),
    hours: hours.padStart(2, "0"),
    minutes: minutes.padStart(2, "0"),
  };
};

export default useHeatmapData;
